/**
 * The parts of streams log forwarding that do not need streams at runtime, kept out of
 * `forwardStreamsLogs` because importing that module runs its `import "amazon-connect-streams"`:
 * 1.5 MB into whichever chunk reaches it, the global `connect` object, and a 30-minute log-roll
 * `setInterval`, all at module scope. `createAgentSdk` defers streams behind a dynamic import to
 * avoid exactly that, and `logger-init` is eager outright.
 */

/** The string-literal union behind `connect.LogLevel`, so nothing here reads the enum at runtime. */
export type ConnectLogLevel = `${connect.LogLevel}`;

/**
 * Ascending by severity, which is all callers need: they compare their own threshold against their
 * own reading of an entry's level, never against streams' internal `LogLevelOrder` numbers.
 */
export const LEVELS_BY_SEVERITY: readonly string[] = Object.freeze([
    "TEST",
    "TRACE",
    "DEBUG",
    "INFO",
    "LOG",
    "WARN",
    "ERROR",
    "CRITICAL",
] satisfies readonly ConnectLogLevel[]);

/**
 * Whether an untrusted value — in practice a feature-flag value — names a streams level.
 */
export function isStreamsLogLevel(value: unknown): value is ConnectLogLevel {
    // A list rather than a keyed object with `in`, which walks the prototype chain: `"toString"` would
    // pass, and an unrecognised threshold clears every comparison and forwards everything.
    return typeof value === "string" && LEVELS_BY_SEVERITY.includes(value);
}

/** Names streams as the source of a log entry or a metric, so the two join up. */
export const STREAMS_SOURCE = "amazon-connect-streams";

/** Holds the streams level an entry was logged at, and marks the entry as forwarded. */
export const FORWARDED_LEVEL_FIELD = "connectLevel";

/**
 * Whether a log call's merge object is an entry `forwardStreamsLogs` forwarded
 */
export function isForwardedStreamsEntry(value: unknown): boolean {
    //  `Object.hasOwn` rather than `in`, so an inherited property cannot pass for the marker.
    return typeof value === "object" && value !== null && Object.hasOwn(value, FORWARDED_LEVEL_FIELD);
}
